import { memo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Add } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { Button, Chip, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { GhostIntegrations } from '@mint-sdk/api-integrations';
import { Link } from '@mint/routing';

import { INTEGRATION_TYPES_NEW } from '../../../constants/ComponentsConstants';
import {
  useIntegrationsPermissions,
  useIntegrationsTabs,
} from '../../../hooks/index.js';
import {
  useIntegrationPaths,
  useIntegrationsIndexRouteApi,
} from '../../../hooks/useIntegrationsRouteApi.js';
import { Platform } from '../../../types/index.js';
import {
  isVirtualPlatformIntegrated,
  setIntegratedGhostIds,
} from '../../../utils/platforms.js';
import PlatformIcon from '../../_atoms/PlatformIcon/index.js';

type Props = {
  integration: Platform;
  onIntegrateButtonClick: (integrationId: number) => void;
};

const getFixedPlatformName = (integration: Platform) => {
  if (integration.platform === 'ga4') {
    return 'google_analytics';
  }
  return integration.platform;
};

const IntegrationCard = ({ integration, onIntegrateButtonClick }: Props) => {
  const { t } = useTranslation('@myn/integrations_v2');
  const navigate = useIntegrationsIndexRouteApi().useNavigate();
  const paths = useIntegrationPaths();
  const virtualPlatformIsIntegrated = isVirtualPlatformIntegrated(integration);

  const platformName = getFixedPlatformName(integration);

  const { initialTab } = useIntegrationsTabs(integration);

  const isAllowed = useIntegrationsPermissions('view', integration.category);
  const isUserReadOnly = !useIsAllowedPermission('integrations:modify');
  const { mutateAsync: integrateGhostMutateAsync } =
    GhostIntegrations.integrateGhost();
  const chipProps = INTEGRATION_TYPES_NEW(t)[integration.type]?.chipProps;
  const isIntegrated = [
    integration.isIntegrated,
    virtualPlatformIsIntegrated,
  ].some(Boolean);

  const onIntegrateClick = async () => {
    if (['one_way', 'two_way'].includes(integration.type)) {
      navigate({
        to: paths.integrationType,
        params: {
          integration: platformName,
          tabName: initialTab.id,
          type: 'add',
        },
      });
      return;
    }

    await integrateGhostMutateAsync({
      data: {
        name: integration?.platform || '',
        type: integration.categoryType,
        provider: integration?.platform || '',
      },
    });
    onIntegrateButtonClick(integration.id);
    setIntegratedGhostIds(integration.id);
  };

  return (
    <div
      data-testid={integration.id}
      aria-label="platform card"
      className={cn(
        'box-border flex flex-col items-start rounded-sm bg-ui-secondary p-6 no-underline',
        isIntegrated
          ? 'border-2 border-success-main'
          : 'border border-ui-active',
        !isAllowed && 'pointer-events-none opacity-60',
      )}
    >
      <div className="mb-4 flex w-full justify-between">
        <PlatformIcon
          logo={integration.logo}
          counter={integration.integrationsCount ?? undefined}
        />
        <Chip color={chipProps.color}>
          {chipProps.icon}
          {chipProps.label}
        </Chip>
      </div>
      <h4 className={cn(typographyVariants({ variant: 'h4' }), 'mb-1 h-18')}>
        {integration.label}
      </h4>
      {!integration.isIntegrated && !virtualPlatformIsIntegrated ? (
        <Button
          onClick={onIntegrateClick}
          color="secondary"
          disabled={isUserReadOnly}
          className="w-full"
          data-dd-action-name="integrate platform"
        >
          <Add className="mr-2" />
          {t('Integrate')}
        </Button>
      ) : (
        <Link
          to={paths.integrationType}
          params={{
            integration: platformName,
            tabName: initialTab.id,
            type: 'add',
          }}
          style={{
            textDecoration: 'none',
            width: '100%',
          }}
          data-dd-action-name="open manage integration"
        >
          <Button
            disabled={!isAllowed}
            variant="outlined"
            className="w-full"
            data-dd-action-name="manage integration"
          >
            {t('Manage Integration')}
          </Button>
        </Link>
      )}
    </div>
  );
};

export default memo(IntegrationCard);
